import * as React from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  getShowcase,
  removeShowcase,
  saveShowcase,
  showcasePost,
  type ShowcaseDeployment,
} from "../../../services/adminOverviewApi";
import {
  liveBodyTextClassName,
  liveCardBodyClassName,
  liveCardClassName,
  liveCardTitleClassName,
  liveOptionTitleClassName,
  liveSmallTextClassName,
} from "../LiveStrategies/liveStrategiesUi";

const btn = "shadow-none";

/** Pick which of your Launchpad strategies the public Telegram channel shows, preview and post. */
export default function AdminShowcasePanel() {
  const [rows, setRows] = React.useState<ShowcaseDeployment[]>([]);
  const [channelOk, setChannelOk] = React.useState(true);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [msg, setMsg] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [titles, setTitles] = React.useState<Record<string, string>>({});

  const load = React.useCallback(async () => {
    try {
      const res = await getShowcase();
      setRows(res.deployments);
      setChannelOk(res.channel_configured);
      setTitles(Object.fromEntries(res.deployments.map((d) => [d.deployment_id, d.showcase?.title ?? d.name ?? ""])));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Couldn't load.");
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const save = (d: ShowcaseDeployment, patch: Partial<NonNullable<ShowcaseDeployment["showcase"]>>) =>
    run(async () => {
      const base = d.showcase ?? { title: titles[d.deployment_id] || d.name || "Strategy", blurb: null, daily: true, weekly: true, enabled: true };
      await saveShowcase(d.deployment_id, { ...base, title: titles[d.deployment_id] || base.title, ...patch });
      await load();
    });

  const post = (period: "daily" | "weekly", action: "preview" | "send") =>
    run(async () => {
      const res = await showcasePost(period, action);
      setPreview(res.text);
      setMsg(action === "send" ? (res.sent ? "Posted to the channel." : res.reason ?? "Not posted.") : res.reason ?? null);
    });

  return (
    <div className="flex flex-col gap-4" data-admin-showcase="">
      <p className={cn(liveBodyTextClassName, "m-0 max-w-3xl")}>
        Your Launchpad strategies shown in the public Telegram channel. Posts go out after the daily summary
        hour, and a weekly recap on Mondays. Results are in R and win rate, never money.
        {!channelOk ? " Set TELEGRAM_SHOWCASE_CHAT_ID and add the bot as a channel admin to post." : ""}
      </p>

      <div className={cn(liveCardClassName, "overflow-hidden p-0")}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {["Strategy", "Public name", "Daily", "Weekly", ""].map((h) => (
                <TableHead key={h} className="font-[family-name:var(--font-primary)] text-xs font-medium text-gray-500 dark:text-white">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.deployment_id}>
                <TableCell className="min-w-64">
                  <div className={liveOptionTitleClassName}>{d.name}</div>
                  <div className={liveSmallTextClassName}>{d.symbol} {d.candle_type} · {d.mode} · {d.status}</div>
                </TableCell>
                <TableCell>
                  <Input value={titles[d.deployment_id] ?? ""} className="h-9 max-w-64"
                    onChange={(e) => setTitles({ ...titles, [d.deployment_id]: e.target.value })}
                    onBlur={() => d.showcase && void save(d, {})} />
                </TableCell>
                <TableCell>
                  <Checkbox checked={!!d.showcase?.daily} disabled={!d.showcase || busy}
                    onCheckedChange={(v) => void save(d, { daily: v === true })} aria-label="Post daily" />
                </TableCell>
                <TableCell>
                  <Checkbox checked={!!d.showcase?.weekly} disabled={!d.showcase || busy}
                    onCheckedChange={(v) => void save(d, { weekly: v === true })} aria-label="Post weekly" />
                </TableCell>
                <TableCell className="text-right">
                  {d.showcase ? (
                    <Button type="button" size="sm" variant="outline-red" data-font="primary" className={btn} disabled={busy}
                      onClick={() => void run(async () => { await removeShowcase(d.deployment_id); await load(); })}>
                      <span className="normal-case tracking-normal">Remove</span>
                    </Button>
                  ) : (
                    <Button type="button" size="sm" variant="outline-brand" data-font="primary" className={btn} disabled={busy}
                      onClick={() => void save(d, {})}>
                      <span className="normal-case tracking-normal">Showcase</span>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline-gray" data-font="primary" className={btn} disabled={busy}
          onClick={() => void post("daily", "preview")}><span className="normal-case tracking-normal">Preview daily</span></Button>
        <Button type="button" size="sm" variant="outline-gray" data-font="primary" className={btn} disabled={busy}
          onClick={() => void post("weekly", "preview")}><span className="normal-case tracking-normal">Preview weekly</span></Button>
        <Button type="button" size="sm" variant="outline-brand" data-font="primary" className={btn} disabled={busy || !channelOk}
          onClick={() => void post("daily", "send")}><span className="normal-case tracking-normal">Post daily now</span></Button>
        <Button type="button" size="sm" variant="outline-brand" data-font="primary" className={btn} disabled={busy || !channelOk}
          onClick={() => void post("weekly", "send")}><span className="normal-case tracking-normal">Post weekly now</span></Button>
      </div>
      {msg ? <p className={cn(liveBodyTextClassName, "m-0")}>{msg}</p> : null}
      {preview ? (
        <div className={cn(liveCardClassName, liveCardBodyClassName)}>
          <span className={liveCardTitleClassName}>Preview</span>
          <pre className={cn(liveBodyTextClassName, "m-0 whitespace-pre-wrap")}>{preview.replace(/<[^>]+>/g, "")}</pre>
        </div>
      ) : null}
    </div>
  );
}
